import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { Filter } from '@shared/models/filter';
import { GetSearchResultsResponse } from '@shared/models/get-search-results-response';
import { ModuleTypes } from '@shared/models/modules.enum';
import { PaginatedApiResponse } from '@shared/models/paginated-api-response';
import { ProductType } from '@shared/models/product-type';
import { EventResponse } from '@shared/models/search/entertainment/event-response';
import { SearchResult } from '@shared/models/search/search-result';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { ISiteSettingsSwitches } from '@shared/shared/interfaces/site-settings.interfaces';
import {
  Card,
  CardStyleEnum,
  FollowButton,
  FollowButtonStyleEnum,
  NotificationTexts,
  PreferenceTracking,
} from '@shared/shared/modules/card/models/card';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { getEntertainmentSearchResults } from '@shared/store/entertainment';
import { extractId, replaceTemplateKeys } from '@shared/utils/url.lib';
import { Angulartics2Segment } from 'angulartics2';
import { Observable, filter, map, switchMap, take } from 'rxjs';
import { SiteSettingsService } from '../site-settings.service';

@Injectable({ providedIn: 'root' })
export class EntertainmentSearchService {
  public entertainmentsPhase11Switch: boolean;

  constructor(
    private http: HttpClient,
    private angulartics2Segment: Angulartics2Segment,
    private customisableVariablesService: CustomisableVariablesService,
    private siteSettingsService: SiteSettingsService,
    private siteConfService: SiteConfigService,
    private ngrxStore: Store
  ) {
    this.siteSettingsService
      .fetchSiteSettings()
      .pipe(
        filter((settings) => !!settings.id),
        take(1)
      )
      .subscribe((siteSettings) => {
        this.entertainmentsPhase11Switch = siteSettings.switches.entertainment_redesign_phase_1_1;
      });
  }

  public getEntertainmentResults(
    searchPhrase = '',
    selectedFilters: Filter[] = [],
    page = 1
  ): Observable<GetSearchResultsResponse> {
    if (this.entertainmentsPhase11Switch) {
      return this.ngrxStore
        .select(getEntertainmentSearchResults)
        .pipe(map((res) => ({ items: res }) as GetSearchResultsResponse));
    }

    const pageSize = 12;
    let params = new HttpParams({
      fromObject: {
        search: searchPhrase,
        page,
        page_size: pageSize,
      },
    });
    selectedFilters.forEach((selectedFilter) => (params = params.append(selectedFilter.name, selectedFilter.value)));

    this.angulartics2Segment.eventTrack('search', {
      query: searchPhrase,
      module: ModuleTypes.Entertainment,
    });

    return this.http.get(replaceTemplateKeys(ApiEndpoint.SEARCH, { module: 'entertainment' }), { params }).pipe(
      switchMap((data: PaginatedApiResponse<any>) =>
        this.customisableVariablesService.variables.pipe(
          take(1),
          map((customisableVariables) => {
            const searchResultCards = data.results
              .filter((res) => res.object_type === 'event')
              .map((res) => this.mapToEventSearchResult(res as EventResponse, customisableVariables));

            return {
              items: searchResultCards,
              page,
              pageSize,
              canLoadMore: !!data.next,
              totalItems: data.count ? data.count : searchResultCards.length,
            };
          })
        )
      )
    );
  }

  private mapToEventSearchResult = (
    result: EventResponse,
    customisableVariables: CustomisableVariables
  ): SearchResult => {
    const componentData = this.siteConfService.getComponentData('searchResults');
    const cardConfig: Card = componentData.default;

    let cardStyle = null,
      darkTheme = false,
      showMoreInfo = false;

    if (cardConfig) {
      cardStyle = cardConfig.cardStyle || CardStyleEnum.float;
      darkTheme = cardConfig.darkTheme || false;
      showMoreInfo = cardConfig.showMoreInfo || false;
    }

    const description = Object.prototype.hasOwnProperty.call(result, 'status_localised')
      ? result.status_localised
      : result.status;

    const switches = this.siteSettingsService.settings.switches;
    const followButton: FollowButton | null =
      switches.preferences_artist || switches.preferences_interests ? this.getFollowButton(switches, result) : null;

    return {
      // config
      uuid: result.url,
      id: extractId(result.url),
      type: result.status === 'available' ? 'event-booking' : 'event-request',
      imageUrl: result.hero_images?.[0] ? result.hero_images[0].image : null,
      url: `/entertainment/events/${result.id.toString()}`,
      cardStyle,
      darkTheme,
      showMoreInfo,
      // content
      ribbon: null,
      // followButton
      followButton,
      // body
      body: {
        heading: result.name,
        text: result.location,
        status: {
          healthy: result.status === 'available',
          description: description ? description : null,
        },
        price: result.cheapest_ticket_type,
      },
    };
  };

  private getFollowButton(switches: ISiteSettingsSwitches, result: EventResponse) {
    if (!switches.preferences_follow_events_and_entertainment) {
      return switches.preferences_artist && result.artists?.length === 1
        ? this.setFollowButton(result, 'artist')
        : this.setFollowButton(result, 'favourite');
    } else {
      if (switches.preferences_interests && result?.primary_interest) {
        return this.setFollowButton(result, 'interest');
      }
      return switches.preferences_artist && result.artists?.length === 1
        ? this.setFollowButton(result, 'artist')
        : null;
    }
  }

  private getPreferenceTracking(result: EventResponse, type: string): PreferenceTracking {
    if (type === 'artist' || type === 'favourite') {
      const eventName =
        type === 'artist'
          ? 'Entertainment page - Follow / Unfollow artist button on the card'
          : 'Preferences_Favourite event selected';
      return {
        eventName,
        data: {
          id: type === 'artist' ? result?.artists[0]?.id : result?.id,
          name: type === 'artist' ? result?.artists[0]?.name : result?.name,
          category: type === 'artist' ? result?.category : undefined,
        },
      };
    } else if (type === 'interest') {
      return {
        eventName: 'Entertainment Page - Follow / Unfollow button on the card',
        data: {
          id: result?.id,
          name: result?.name,
          category: result?.category,
          eventName: result?.name,
          primaryInterest: result?.primary_interest?.name,
        },
      };
    }
  }

  private setFollowButton(result: EventResponse, type: string): FollowButton {
    let preferred;
    let preferencesOffText = '';
    let product: FollowButton['product'];
    let favourited: boolean;
    const trackingData = this.getPreferenceTracking(result, type);
    let notificationTexts: NotificationTexts;

    switch (type) {
      case 'artist':
        preferred = result?.artists[0]?.is_preferred;
        preferencesOffText = 'Preferences:FollowArtist';
        product = { type: ProductType.Artist, id: result?.artists[0]?.id, name: result?.artists[0]?.name };
        favourited = undefined;
        notificationTexts = {
          labels: {
            on: 'Preferences:ConfirmFollowArtist',
            off: 'Preferences:ConfirmUnfollowArtist',
            params: {
              artist_name: result?.artists?.[0]?.name,
            },
          },
          ariaLabels: {
            on: 'Preferences:ReaderArtistFollowed',
            off: 'Preferences:ReaderArtistUnfollowed',
          },
        };
        break;
      case 'favourtie':
        preferred = result?.is_preferred;
        preferencesOffText = 'Preferences:Favourite';
        product = { type: ProductType.EntertainmentEvent, id: result?.id, name: result?.name };
        favourited = true;
        notificationTexts = {
          labels: {
            on: 'Preferences:EventAddedNotification',
            off: 'Preferences:EventRemovedNotification',
            params: {
              event_name: result?.name,
            },
          },
          ariaLabels: {
            on: 'Preferences:ReaderEventAdded',
            off: 'Preferences:ReaderEventRemoved',
          },
        };
        break;
      case 'interest':
        preferred = result?.primary_interest?.is_preferred;
        preferencesOffText = 'Preferences:Follow';
        product = {
          type: ProductType.PrimaryInterest,
          id: result?.primary_interest?.id,
          name: result?.primary_interest?.name,
        };
        favourited = false;
        notificationTexts = {
          labels: {
            on: 'Preferences:ConfirmFollow',
            off: 'Preferences:ConfirmUnfollow',
            params: {
              interest_name: result?.primary_interest?.name,
            },
          },
          ariaLabels: {
            on: 'Preferences:ReaderEventAdded',
            off: 'Preferences:ReaderEventRemoved',
          },
        };
        break;
      default:
        break;
    }
    return {
      style: FollowButtonStyleEnum.Round,
      preferred,
      preferenceTracking: trackingData,
      texts: {
        on: type === 'favourite' ? 'Preferences:Favourite' : 'Preferences:Following',
        off: preferencesOffText,
      },
      product,
      favourite: favourited,
      notificationTexts,
    };
  }
}
